import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import fp from 'lodash/fp';
import { ButtonBase, Icon } from '@deerplusofficial/pos-components';
import { color } from '@deerplusofficial/pos-helper';
import Date from './Date';

const weeks = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const Container = styled.div`
  ${props =>
    props.hide &&
    css`
      display: none;
    `};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  padding: 0 8px 8px;
`;

const CurrentMonth = styled.div`
  flex-grow: 1;
  font-size: 13px;
  font-weight: bold;
  text-align: center;
  color: ${color.text.title};
`;

const MonthSelector = styled(ButtonBase)`
  font-size: 11px;
  color: ${color.text.info};
`;

const Body = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  text-align: center;
`;

const Day = styled.th`
  font-size: 11px;
  font-weight: bold;
  color: ${color.text.info};
`;

class Calendar extends Component {
  static propTypes = {
    today: PropTypes.number,
    onMomentChange: PropTypes.func,
    hide: PropTypes.bool,
    startDate: PropTypes.shape(),
    endDate: PropTypes.shape(),
  };

  static defaultProps = {
    today: 1,
    onMomentChange: fp.identity,
    hide: false,
  };

  get dateGroup() {
    const { moment } = this.props;

    const lastDayOfLastMonth = moment
      .clone()
      .subtract(1, 'month')
      .endOf('month')
      .date();
    const firstWeekDay = moment
      .clone()
      .date(1)
      .day();
    const lastDay = moment
      .clone()
      .endOf('month')
      .date();
    const offset = firstWeekDay - 1;
    const totalDayCounts = 42;

    return fp.compose(
      fp.chunk(7),
      fp.flatten
    )([
      fp.range(lastDayOfLastMonth - offset, lastDayOfLastMonth + 1),
      fp.range(1, lastDay + 1),
      fp.range(1, totalDayCounts - lastDay - offset),
    ]);
  }

  onPrevMonthClick = () => {
    const { moment, onMomentChange } = this.props;

    onMomentChange(moment.subtract(1, 'month'));
  };

  onNextMonthClick = () => {
    const { moment, onMomentChange } = this.props;

    onMomentChange(moment.add(1, 'month'));
  };

  renderDateRow = (days, weekDay) => {
    const { moment, onMomentChange, today, startDate, endDate } = this.props;
    return (
      <tr key={`weekday-${weekDay}`}>
        {fp.map(date => {
          let month;
          const isOnPrevMonth = weekDay === 0 && date > 7;
          const isOnNextMonth = weekDay >= 4 && date <= 14;

          if (isOnPrevMonth) {
            month = moment
              .clone()
              .subtract(1, 'month')
              .month();
          } else if (isOnNextMonth) {
            month = moment
              .clone()
              .add(1, 'month')
              .month();
          } else {
            month = moment.month();
          }

          const momentLikeDate = moment
            .clone()
            .month(month)
            .date(date);
          const disabled = fp.size(startDate)
            ? !momentLikeDate.isBetween(startDate, endDate, 'day', '[]')
            : momentLikeDate.isAfter(endDate, 'day');

          return (
            <Date
              key={`date-${date}`}
              moment={moment}
              disabled={disabled}
              onMomentChange={onMomentChange}
              isOnPrevMonth={isOnPrevMonth}
              isOnNextMonth={isOnNextMonth}
              active={!isOnPrevMonth && !isOnNextMonth && date === today}
            >
              {date}
            </Date>
          );
        })(days)}
      </tr>
    );
  };

  render() {
    const { moment, hide } = this.props;

    return (
      <Container hide={hide}>
        <Toolbar>
          <MonthSelector onClick={this.onPrevMonthClick}>
            <Icon icon="chevron-left" />
          </MonthSelector>
          <CurrentMonth>{moment.format('MMM YYYY')}</CurrentMonth>
          <MonthSelector onClick={this.onNextMonthClick}>
            <Icon icon="chevron-right" />
          </MonthSelector>
        </Toolbar>
        <Body>
          <thead>
            <tr>
              {fp.map(week => <Day key={`day-${week}`}>{week}</Day>)(weeks)}
            </tr>
          </thead>
          <tbody>
            {fp.map.convert({ cap: false })(this.renderDateRow)(this.dateGroup)}
          </tbody>
        </Body>
      </Container>
    );
  }
}

export default Calendar;



// WEBPACK FOOTER //
// ./src/components/Calendar/Calendar.js